import { ChevronDown } from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'

import { SEARCH_TYPE_VALUES } from './SearchSelector.constants'
import { useSearchSelector } from './SearchSelector.hooks'
import styles from './SearchSelector.module.css'
import type { SearchSelectorProps, SearchType } from './SearchSelector.types'
import {
	getIcon,
	SearchSelectorOption,
} from './SearchSelectorOption/SearchSelectorOption'

export type { SearchType }

export const SearchSelector = ({
	name = 'searchType',
	onAfterSelect,
	onSearchTypeWillChange,
	...props
}: SearchSelectorProps): JSX.Element => {
	const { t } = useTranslation(['search'])

	const {
		ids,
		rootRef,
		buttonRef,
		selected,
		activeIndex,
		isOpen,
		selectType,
		setActiveIndex,
		toggleOpen,
		onKeyDown,
	} = useSearchSelector({
		...(onAfterSelect !== undefined && { onAfterSelect }),
		...(onSearchTypeWillChange !== undefined && { onSearchTypeWillChange }),
	})

	const label = t(`search-selector.${selected}-trigger`)

	return (
		<div ref={rootRef} className={styles.wrapper}>
			<input name={name} readOnly type="hidden" value={selected} />

			<button
				ref={buttonRef}
				id={ids.comboboxId}
				aria-activedescendant={
					isOpen ? `${ids.baseOptionId}-${activeIndex}` : undefined
				}
				aria-autocomplete="none"
				aria-controls={isOpen ? ids.listboxId : undefined}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				aria-label={t('search-selector.button-aria-label', {
					type: label,
				})}
				className={clsx(styles.trigger, isOpen && styles.triggerOpen)}
				role="combobox"
				type="button"
				onClick={toggleOpen}
				onKeyDown={onKeyDown}
				{...props}
			>
				<span className={styles.triggerMain}>
					{getIcon(selected)}
					<span className={styles.triggerLabel}>{label}</span>
				</span>
				<ChevronDown aria-hidden className={styles.chevron} />
			</button>

			{isOpen && (
				<div className={styles.dropdown}>
					<h2 className={styles.dropdownHeader} id={ids.headingId}>
						{t('search-selector.heading')}
					</h2>
					<ul
						aria-labelledby={ids.headingId}
						aria-multiselectable={false}
						className={styles.listbox}
						id={ids.listboxId}
						role="listbox"
						tabIndex={-1}
					>
						{SEARCH_TYPE_VALUES.map((type, index) => (
							<SearchSelectorOption
								key={type}
								id={`${ids.baseOptionId}-${index}`}
								isActive={index === activeIndex}
								isSelected={type === selected}
								type={type}
								onClick={(): void => selectType(type)}
								onMouseEnter={(): void => setActiveIndex(index)}
								data-testid={`search-mode-selector-${type}-option`}
							/>
						))}
					</ul>
				</div>
			)}
		</div>
	)
}
